// src/services/pdfService.js
import { pdfjs } from "react-pdf";

// ✅ Worker estable para Vite: viene de pdfjs-dist
// (Asegúrate de tener "pdfjs-dist" en dependencies)
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// ✅ Configura el worker una sola vez (en browser)
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}

export const pdfService = {
 
  async pdfToBase64(pdfFile) {
    if (!pdfFile) throw new Error("No PDF file provided");

    if (!pdfFile?.type || pdfFile.type !== "application/pdf") {
      throw new Error("El archivo debe ser un PDF");
    }

    const maxSize = 15 * 1024 * 1024; // 15MB
    if (pdfFile.size > maxSize) {
      throw new Error("El archivo es demasiado grande (máximo 15MB)");
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const result = String(reader.result || "");
          const base64 = result.split(",")[1];
          if (!base64) throw new Error("No se pudo obtener el base64 del PDF");
          resolve(base64);
        } catch (e) {
          reject(new Error("Error convirtiendo PDF a base64: " + (e?.message || e)));
        }
      };

      reader.onerror = () => reject(new Error("Error al leer el archivo PDF"));
      reader.readAsDataURL(pdfFile);
    });
  },

  
  async pdfToImageTiles(pdfFile, options = {}) {
    if (!pdfFile) throw new Error("No PDF file provided");
    if (!pdfFile?.type || pdfFile.type !== "application/pdf") throw new Error("El archivo debe ser un PDF");

    const maxSize = 15 * 1024 * 1024;
    if (pdfFile.size > maxSize) throw new Error("El archivo es demasiado grande (máximo 15MB)");
    if (!pdfjs?.getDocument) throw new Error("PDF.js no está disponible (pdfjs.getDocument missing)");

    const scale = typeof options.scale === "number" ? options.scale : 1.35; // 👈 clave para densidad sin explotar
    const rows = Math.max(1, Number(options.rows || 6)); // 6x6 suele ir bien para planos densos
    const cols = Math.max(1, Number(options.cols || 6));
    const quality = typeof options.quality === "number" ? options.quality : 0.72; // JPEG
    const format = options.format === "png" ? "png" : "jpeg"; // default jpeg

    // 1) leer bytes
    const arrayBuffer = await pdfFile.arrayBuffer();
    const typedArray = new Uint8Array(arrayBuffer);

    // 2) cargar pdf
    const loadingTask = pdfjs.getDocument({
      data: typedArray,
      disableAutoFetch: true,
      disableStream: true,
      isEvalSupported: false,
    });

    let pdf;
    let page;

    try {
      pdf = await loadingTask.promise;
      page = await pdf.getPage(1);

      // 3) render página completa
      const viewport = page.getViewport({ scale });

      const fullCanvas = document.createElement("canvas");
      const fullCtx = fullCanvas.getContext("2d", { alpha: false });

      fullCanvas.width = Math.floor(viewport.width);
      fullCanvas.height = Math.floor(viewport.height);

      fullCtx.fillStyle = "#ffffff";
      fullCtx.fillRect(0, 0, fullCanvas.width, fullCanvas.height);

      await page.render({
        canvasContext: fullCtx,
        viewport,
        intent: "display",
      }).promise;

      // 4) tiles
      const tileW = Math.floor(fullCanvas.width / cols);
      const tileH = Math.floor(fullCanvas.height / rows);

      const tiles = [];

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const sx = c * tileW;
          const sy = r * tileH;

          // último tile toma el sobrante
          const sw = c === cols - 1 ? fullCanvas.width - sx : tileW;
          const sh = r === rows - 1 ? fullCanvas.height - sy : tileH;

          const tileCanvas = document.createElement("canvas");
          const tileCtx = tileCanvas.getContext("2d", { alpha: false });

          tileCanvas.width = sw;
          tileCanvas.height = sh;

          tileCtx.fillStyle = "#ffffff";
          tileCtx.fillRect(0, 0, sw, sh);

          tileCtx.drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, sw, sh);

          const mime = format === "png" ? "image/png" : "image/jpeg";
          const imageDataUrl =
            format === "png" ? tileCanvas.toDataURL(mime) : tileCanvas.toDataURL(mime, quality);

          tiles.push({
            imageDataUrl,
            row: r,
            col: c,
            rows,
            cols,
          });
        }
      }

      return {
        tiles,
        meta: {
          width: fullCanvas.width,
          height: fullCanvas.height,
          scale,
          rows,
          cols,
          format,
          quality,
        },
      };
    } catch (err) {
      throw new Error("Error convirtiendo PDF a tiles: " + (err?.message || err));
    } finally {
      try {
        page?.cleanup?.();
      } catch {}
      try {
        pdf?.cleanup?.();
      } catch {}
      try {
        loadingTask?.destroy?.();
      } catch {}
    }
  },

  validatePDF(file) {
    if (!file) return { valid: false, error: "No se seleccionó ningún archivo" };

    if (!file?.type || file.type !== "application/pdf") {
      return { valid: false, error: "El archivo debe ser un PDF" };
    }

    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: "El archivo es demasiado grande (máximo 15MB)" };
    }

    return { valid: true };
  },

  async pdfToPreviewImage(pdfFile) {
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.4 });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: ctx,
    viewport,
  }).promise;

  // ✅ Convert canvas to JPG Blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      "image/jpeg",
      0.8
    );
  });
}

};

export default pdfService;
